import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Archive, Copy } from 'lucide-react';

import { Switch, ToggleField, RadioCardGroup, TextField, Textarea } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  Chip,
  ConfirmModal,
  FormActions,
  Modal,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { isNotFound } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import {
  useArchiveUpdateMutation,
  useCreateUpdateMutation,
  useGetUpdateQuery,
  useUpdateUpdateMutation,
} from '~/modules/query/featureFlags';

import {
  BADGES,
  BADGE_HELP,
  BADGE_LABELS,
  BADGE_TONES,
  DEFAULT_LEARN_MORE_LABEL,
  DEFAULT_LINK_LABEL,
  FLAG_KEY_RE,
  FLAG_STATUS_LABELS,
  flagStatus,
} from '~/components/Constants/featureFlagsConstants';
import AudienceEditor from '~/components/pages/feature-flags/AudienceEditor';
import { cleanConfig, errorText, scheduleError } from '~/components/pages/feature-flags/common';
import { EnvStatusField, ValueToggles } from '~/components/pages/feature-flags/commonUi';
import { useReference } from '~/components/pages/feature-flags/useReference';
import type { Reference } from '~/components/pages/feature-flags/common';
import type {
  Badge,
  LinkStyle,
  ProductUpdate,
  Schedule,
  UpdateEnvConfig,
  UpdateInput,
} from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

type BadgeChoice = Badge | 'none';

interface Draft {
  key: string;
  products: string[];
  title: string;
  description: string;
  badge: BadgeChoice;
  linkUrl: string;
  linkLabel: string;
  linkStyle: LinkStyle;
  learnMoreUrl: string;
  learnMoreLabel: string;
  learnMoreStyle: LinkStyle;
  hasToggle: boolean;
  envs: Record<string, UpdateEnvConfig & Schedule>;
}

const blankEnv = (): UpdateEnvConfig & Schedule => ({
  enabled: false,
  startsAt: null,
  endsAt: null,
  defaultValue: false,
  defaultChoice: false,
  rules: [],
});

const toDraft = (reference: Reference, update: ProductUpdate | null): Draft => ({
  key: update?.key ?? '',
  products: update?.products ?? [],
  title: update?.title ?? '',
  description: update?.description ?? '',
  badge: update?.badge ?? 'none',
  linkUrl: update?.linkUrl ?? '',
  linkLabel: update?.linkLabel || DEFAULT_LINK_LABEL,
  linkStyle: update?.linkStyle ?? 'link',
  learnMoreUrl: update?.learnMoreUrl ?? '',
  learnMoreLabel: update?.learnMoreLabel || DEFAULT_LEARN_MORE_LABEL,
  learnMoreStyle: update?.learnMoreStyle ?? 'link',
  hasToggle: update?.hasToggle ?? false,
  envs: Object.fromEntries(
    reference.environments.map((e) => [e.key, update?.envs[e.key] ?? blankEnv()]),
  ),
});

const toPayload = (draft: Draft): UpdateInput => ({
  key: draft.key.trim(),
  products: draft.products,
  title: draft.title.trim(),
  description: draft.description.trim(),
  badge: draft.badge === 'none' ? null : draft.badge,
  linkUrl: draft.linkUrl.trim() || null,
  linkLabel: draft.linkLabel.trim() || DEFAULT_LINK_LABEL,
  linkStyle: draft.linkStyle,
  learnMoreUrl: draft.learnMoreUrl.trim() || null,
  learnMoreLabel: draft.learnMoreLabel.trim() || DEFAULT_LEARN_MORE_LABEL,
  learnMoreStyle: draft.learnMoreStyle,
  hasToggle: draft.hasToggle,
  envs: Object.fromEntries(Object.entries(draft.envs).map(([k, c]) => [k, cleanConfig(c)])),
});

const UPDATE_LABELS = { on: 'Display', off: 'Hide' };
const LINK_STYLE_OPTIONS: { value: LinkStyle; label: string; description: string }[] = [
  { value: 'link', label: 'Link', description: 'The link text, in the accent color.' },
  { value: 'button', label: 'Button', description: 'A filled button with the link text.' },
];
const BADGE_OPTIONS = [
  { value: 'none' as BadgeChoice, label: 'None', description: 'No label, like an announcement.' },
  ...BADGES.map((b) => ({
    value: b as BadgeChoice,
    label: BADGE_LABELS[b],
    description: BADGE_HELP[b],
  })),
];

const KEY_MESSAGE =
  'Use 2 to 63 lowercase letters, numbers and dashes, starting with a letter or number.';

const FORM_ID = 'update-form';
const LIST = routes.featureFlagsSection('updates');

export default function UpdatePage() {
  const navigate = useNavigate();
  const url = useUrlParams();
  const { id } = useParams<{ id: string }>();
  const updateId = id ?? '';
  const isNew = !id;
  const ref = useReference();
  const { environments, products, attributes } = ref.reference;

  const query = useGetUpdateQuery(updateId, { skip: isNew });
  const record = query.currentData;
  // `reference` is read at seed time only: a new list must not wipe unsaved edits (a save bumps `version`).
  const { draft, setDraft, patch, dirty, markSaved } = useRecordDraft<ProductUpdate | null, Draft>(
    ref.loaded ? (isNew ? null : record) : undefined,
    {
      id: id ?? 'new',
      version: record?.version,
      toDraft: (update) => toDraft(ref.reference, update),
    },
  );
  const guard = useUnsavedGuard(dirty);

  const schema = useMemo(
    () =>
      z.object({
        key: z.string().trim().regex(FLAG_KEY_RE, KEY_MESSAGE),
        title: z.string().trim().min(1, 'Enter a title.'),
        products: z.array(z.string()).min(1, 'Choose at least one product.'),
        envs: z.custom<Draft['envs']>().superRefine((envs, ctx) => {
          const message = scheduleError(envs, environments);
          if (message) ctx.addIssue({ code: 'custom', message });
        }),
      }),
    [environments],
  );
  const check = useFormValidation(schema, draft, (field) =>
    field === 'products'
      ? `update-product-${products[0]?.key}`
      : field === 'envs'
        ? 'update-envs-error'
        : `update-${field}`,
  );

  const [createUpdate] = useCreateUpdateMutation();
  const [updateUpdate] = useUpdateUpdateMutation();
  const [archiveUpdate] = useArchiveUpdateMutation();
  const createdId = useRef<string | undefined>(undefined);
  const [archiving, setArchiving] = useState(false);
  const [previewOn, setPreviewOn] = useState(true);
  const [copyFrom, setCopyFrom] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);

  const save = useRecordSave<Draft>({
    save: async (d) => {
      const payload = toPayload(d);
      if (isNew) createdId.current = (await createUpdate(payload).unwrap()).id;
      else if (record)
        await updateUpdate({
          id: record.id,
          data: { ...payload, version: record.version },
        }).unwrap();
    },
    success: isNew ? 'Product update created' : 'Product update saved',
    failure: 'The product update could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(createdId.current ? routes.featureFlagsRecord('updates', createdId.current) : LIST, {
      replace: true,
    });
  };

  const archive = () => {
    if (!record) return;
    archiveUpdate(record.id)
      .unwrap()
      .then(() => {
        toast.success('Product update archived');
        guard.release();
        navigate(LIST, { replace: true });
      })
      .catch((err) =>
        toast.error(errorText(err, 'The product update could not be archived. Try again.')),
      );
    setArchiving(false);
  };

  const requestedEnv = url.get('env');
  const active =
    environments.find((e) => e.key === requestedEnv)?.key ?? environments[0]?.key ?? '';
  const current = draft?.envs[active] ?? blankEnv();
  // The preview's switch starts where an eligible user's does: the active environment's default; clicking it only tries it out.
  useEffect(() => {
    setPreviewOn(current.defaultChoice);
  }, [current.defaultChoice]);
  const patchActive = (next: UpdateEnvConfig & Schedule) =>
    setDraft((d) => ({ ...d, envs: { ...d.envs, [active]: next } }));
  const activeName = environments.find((e) => e.key === active)?.name ?? active;
  const otherEnvs = environments.filter((e) => e.key !== active);
  const copySource = otherEnvs.find((e) => e.key === copyFrom)?.key ?? otherEnvs[0]?.key ?? '';
  const ruleCount = (key: string) => draft?.envs[key]?.rules.length ?? 0;
  const copyRules = () => {
    const src = draft?.envs[copySource];
    if (src) patchActive({ ...current, rules: cleanConfig(src).rules });
    setCopyOpen(false);
  };
  const set = <K extends keyof Draft>(name: K, value: Draft[K]) =>
    patch({ [name]: value } as Partial<Draft>);

  const title = isNew ? 'Create product update' : record?.title || 'Product update';
  const notFound = !isNew && isNotFound(query.error);
  const loadError = ref.loaded ? null : ref.error;
  const failed = (query.isError && !notFound) || !!loadError;
  const badge = draft && draft.badge !== 'none' ? draft.badge : null;

  const sections = draft && (
    <div className="min-w-0 space-y-6">
      <CardSection title="Details">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TextField
            id="update-key"
            testId="update-key"
            label="Product update key"
            required
            code
            readOnly={!isNew}
            value={draft.key}
            onChange={(v) => set('key', v.toLowerCase())}
            error={check.errors.key}
            onBlur={() => check.touch('key')}
            placeholder="new-editor-preview"
            helperText="Used by isUpdateOn in your app. Lowercase letters, numbers and dashes; it cannot change after creation."
          />
          <TextField
            id="update-title"
            testId="update-title"
            label="Title"
            required
            value={draft.title}
            onChange={(value) => set('title', value)}
            error={check.errors.title}
            onBlur={() => check.touch('title')}
            placeholder="New eSign experience"
          />
          <div className="lg:col-span-2">
            <Textarea
              id="update-description"
              testId="update-description"
              label="Short description"
              rows={3}
              maxLength={400}
              value={draft.description}
              onChange={(v) => set('description', v)}
              placeholder="What it is and why someone would use it, in a sentence or two."
            />
          </div>
        </div>
      </CardSection>

      <CardSection
        title="Links"
        description="Both are optional: https:// or a path in the app, like /esign."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TextField
            id="update-link-url"
            testId="update-link-url"
            label="Link"
            code
            value={draft.linkUrl}
            onChange={(v) => set('linkUrl', v)}
            placeholder="/esign or https://…"
          />
          <TextField
            id="update-link-label"
            testId="update-link-label"
            label="Link text"
            disabled={!draft.linkUrl.trim()}
            value={draft.linkLabel}
            onChange={(v) => set('linkLabel', v)}
            placeholder={DEFAULT_LINK_LABEL}
          />
          <div className="lg:col-span-2">
            <RadioCardGroup
              name="update-link-style"
              ariaLabel="Show the link as"
              value={draft.linkStyle}
              onChange={(v) => set('linkStyle', v)}
              options={LINK_STYLE_OPTIONS}
              disabled={!draft.linkUrl.trim()}
            />
          </div>
          <TextField
            id="update-learn-more-url"
            testId="update-learn-more-url"
            label="Learn more link"
            code
            value={draft.learnMoreUrl}
            onChange={(v) => set('learnMoreUrl', v)}
            placeholder="/help/esign or https://…"
          />
          <TextField
            id="update-learn-more-label"
            testId="update-learn-more-label"
            label="Learn more text"
            disabled={!draft.learnMoreUrl.trim()}
            value={draft.learnMoreLabel}
            onChange={(v) => set('learnMoreLabel', v)}
            placeholder={DEFAULT_LEARN_MORE_LABEL}
          />
          <div className="lg:col-span-2">
            <RadioCardGroup
              name="update-learn-more-style"
              ariaLabel="Show the Learn more link as"
              value={draft.learnMoreStyle}
              onChange={(v) => set('learnMoreStyle', v)}
              options={LINK_STYLE_OPTIONS}
              disabled={!draft.learnMoreUrl.trim()}
            />
          </div>
        </div>
      </CardSection>

      <CardSection title="Products" description="The update is only shown in these products.">
        <ValueToggles
          idPrefix="update-product"
          label="Products"
          hideLabel
          options={products.map((p) => ({ value: p.key, label: p.name }))}
          value={draft.products}
          onChange={(v) => set('products', v)}
          emptyText="Create a product before creating a product update."
        />
        {check.errors.products && (
          <p className="mt-2 text-xs text-error-600 dark:text-error-400">{check.errors.products}</p>
        )}
      </CardSection>

      <CardSection
        title="Badge"
        description="The badge is a label. Publication and this update's audience rules decide who sees the card."
      >
        <RadioCardGroup
          name="update-badge"
          ariaLabel="Badge"
          value={draft.badge}
          onChange={(v) => set('badge', v)}
          options={BADGE_OPTIONS}
        />
      </CardSection>

      <CardSection
        title="Environments"
        description="Each environment has its own status, schedule, rules and default. A disabled environment, or one outside its schedule, hides the card and serves the preference Off to everyone. Edits to every environment save together."
      >
        {environments.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Add an environment on the Environments page to configure this update.
          </p>
        ) : (
          <div className="space-y-4">
            {check.errors.envs && (
              <p
                id="update-envs-error"
                tabIndex={-1}
                role="alert"
                className="text-sm text-error-600 dark:text-error-400"
              >
                {check.errors.envs}
              </p>
            )}
            <RadioCardGroup
              name="update-env"
              ariaLabel="Product update environment"
              value={active}
              onChange={(id) => url.set({ env: id })}
              options={environments.map((e) => {
                const c = draft.envs[e.key] ?? blankEnv();
                return {
                  value: e.key,
                  label: e.name,
                  description: FLAG_STATUS_LABELS[flagStatus(c)],
                  testId: `update-env-${e.key}`,
                  end: (
                    <Switch
                      checked={c.enabled}
                      onChange={(enabled) =>
                        setDraft((d) => ({
                          ...d,
                          envs: { ...d.envs, [e.key]: { ...c, enabled } },
                        }))
                      }
                      ariaLabel={`Enabled in ${e.name}`}
                      testId={`update-enabled-${e.key}`}
                    />
                  ),
                };
              })}
            />
            <div className="space-y-4">
              <EnvStatusField
                envKey={active}
                envName={activeName}
                idPrefix="update"
                value={current}
                onChange={patchActive}
                aside={
                  <>
                    <ToggleField
                      id="update-has-toggle"
                      testId="update-has-toggle"
                      label="User toggle"
                      text="Let users turn it on or off"
                      checked={draft.hasToggle}
                      onChange={(v) => set('hasToggle', v)}
                      helperText="Every environment. Off: an announcement only, and isUpdateOn returns Off."
                    />
                    {draft.hasToggle && (
                      <ToggleField
                        id="update-audience-default-choice"
                        testId="update-audience-default-choice"
                        label="Before a user chooses"
                        text={current.defaultChoice ? 'On by default' : 'Off by default'}
                        helperText="Saved On and Off choices take precedence."
                        checked={current.defaultChoice}
                        onChange={(defaultChoice) => patchActive({ ...current, defaultChoice })}
                      />
                    )}
                  </>
                }
              />
              {current.enabled && (
                <AudienceEditor
                  idPrefix="update-audience"
                  value={current}
                  onChange={patchActive}
                  attributes={attributes}
                  products={products}
                  labels={UPDATE_LABELS}
                  ruleActions={
                    otherEnvs.length > 0 &&
                    current.rules.length === 0 && (
                      <Button
                        variant="outline"
                        startIcon={<Copy className="size-4" aria-hidden="true" />}
                        onClick={() => setCopyOpen(true)}
                        testId="update-copy"
                      >
                        Copy rules
                      </Button>
                    )
                  }
                />
              )}
            </div>
          </div>
        )}
      </CardSection>
    </div>
  );
  const formBlock = (
    <form id={FORM_ID} noValidate onSubmit={submit}>
      <Card>
        <CardBody className="space-y-6">
          {save.error && <Alert variant="error" message={save.error} />}
          {sections}
        </CardBody>
        <CardFooter>
          <FormActions
            form={FORM_ID}
            discardHref={LIST}
            {...(isNew
              ? { saveLabel: 'Create update', busyLabel: 'Creating…' }
              : { disabled: !dirty, disabledReason: 'No changes to save' })}
            busy={save.busy}
            saveTestId="update-save"
          />
        </CardFooter>
      </Card>
    </form>
  );

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Product updates', href: LIST },
        { label: title },
      ]}
      actions={
        record
          ? [
              {
                label: 'Archive update',
                kind: 'secondary',
                testId: 'update-archive',
                startIcon: <Archive className="size-4" aria-hidden="true" />,
                onClick: () => setArchiving(true),
              },
            ]
          : undefined
      }
      loading={!draft}
      notFound={notFound}
      notFoundTitle="Product update not found"
      backTo={LIST}
      backLabel="Back to product updates"
      error={
        failed
          ? { what: 'the product update', message: queryErrorMessage(query.error ?? loadError) }
          : null
      }
      onRetry={() => {
        if (query.isError) query.refetch();
        ref.refetch();
      }}
      guard={guard}
      testId="update"
    >
      {draft && (
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          <div className="min-w-0 space-y-6 xl:flex-1">{formBlock}</div>

          <aside className="min-w-0 xl:sticky xl:top-0 xl:w-96 xl:shrink-0">
            <Card className="p-5">
              <CardSection
                title="Preview"
                description="How the card looks in the Product Updates panel."
              >
                <div className="space-y-3 rounded-lg border border-gray-200 p-5 dark:border-gray-800">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-base font-semibold text-gray-800 dark:text-white/90">
                      {draft.title || 'Title'}
                    </p>
                    {badge && <Chip label={BADGE_LABELS[badge]} tone={BADGE_TONES[badge]} dot />}
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {draft.description || 'Short description'}
                    </p>
                    {draft.hasToggle && (
                      <Switch
                        checked={previewOn}
                        onChange={setPreviewOn}
                        ariaLabel={`Preview toggle for ${draft.title || 'this update'}`}
                      />
                    )}
                  </div>
                  {(draft.linkUrl.trim() || draft.learnMoreUrl.trim()) && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      {[
                        draft.linkUrl.trim() && {
                          label: draft.linkLabel || DEFAULT_LINK_LABEL,
                          style: draft.linkStyle,
                        },
                        draft.learnMoreUrl.trim() && {
                          label: draft.learnMoreLabel || DEFAULT_LEARN_MORE_LABEL,
                          style: draft.learnMoreStyle,
                        },
                      ].map(
                        (l, i) =>
                          l && (
                            <span
                              key={i}
                              className={
                                l.style === 'button'
                                  ? 'inline-flex rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white'
                                  : 'text-sm font-semibold text-brand-500 dark:text-brand-400'
                              }
                            >
                              {l.label}
                            </span>
                          ),
                      )}
                    </div>
                  )}
                </div>
              </CardSection>
            </Card>
          </aside>
        </div>
      )}

      <Modal
        isOpen={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy rules"
        description={`Copy another environment's rules into ${activeName}. Nothing is saved until you click Save changes.`}
        testId="update-copy-modal"
        footer={
          <FormActions
            discardLabel="Cancel"
            onDiscard={() => setCopyOpen(false)}
            onSave={copyRules}
            saveLabel="Copy rules"
            disabled={ruleCount(copySource) === 0}
            disabledReason="That environment has no rules"
            saveTestId="update-copy-confirm"
          />
        }
      >
        <RadioCardGroup
          name="update-copy-from"
          ariaLabel="Copy rules from"
          value={copySource}
          onChange={setCopyFrom}
          options={otherEnvs.map((e) => ({
            value: e.key,
            label: e.name,
            description:
              ruleCount(e.key) === 1
                ? '1 rule'
                : ruleCount(e.key)
                  ? `${ruleCount(e.key)} rules`
                  : 'No rules',
            testId: `update-copy-from-${e.key}`,
          }))}
        />
      </Modal>

      <ConfirmModal
        isOpen={archiving}
        title="Archive product update"
        message={`Archive '${record?.title}'? The announcement disappears from the product drawer and its user preference evaluates Off.`}
        confirmLabel="Archive update"
        testId="update-archive-confirm"
        onClose={() => setArchiving(false)}
        onConfirm={archive}
      />
    </RecordPageShell>
  );
}
